import { ReactElement, useMemo, useReducer } from "react";

import { Color, newPage } from "../helpers";
import { Size } from "../Renderer/types";
import { ApiZone, ZoningTaxonomy } from "../types";
import { changeCurrentLabel } from "./reducers/changeCurrentLabel";
import { changeLabelColor } from "./reducers/changeLabelColor";
import { changeZoomLevel } from "./reducers/changeZoomLevel";
import { keyPressed } from "./reducers/keyPressed";
import { mouseDown } from "./reducers/mouseDown";
import { mouseLeave } from "./reducers/mouseLeave";
import { mouseMove } from "./reducers/mouseMove";
import { mouseUp } from "./reducers/mouseUp";
import { resetZones } from "./reducers/resetZones";
import { savePage } from "./reducers/savePage";
import { selectPage } from "./reducers/selectPage";
import { setEditingTable } from "./reducers/setEditingTable";
import { setNoiseReductionLevel } from "./reducers/setNoiseReductionLevel";
import { setTableCells } from "./reducers/setTableCells";
import { starLabel } from "./reducers/starLabel";

import { Action, Dispatch, State, TaxonomyMode, TaxonomyState, ZoomLevel } from "./state";

export const DEFAULT_INITIAL_STATE: State = {
  activePage: 0,
  currentLabel: "",
  currentLabelGroup: "",
  pages: [],
  previousPage: -1,
  taxonomy: {
    mode: TaxonomyMode.Simple,
    orderedLabels: {
      image: [],
      math: [],
      table: [],
      text: [],
    },
    rawLabels: {},
  },
  editTable: null,
  zoom: ZoomLevel["1x"],
  tableZoom: ZoomLevel["1x"],
  showZoneLabels: false
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "CHANGE CURRENT LABEL":
      return changeCurrentLabel(state, action);
    case "CHANGE LABEL COLOR":
      return changeLabelColor(state, action);
    case "CHANGE ZOOM LEVEL":
      return changeZoomLevel(state, action);
    case "KEY PRESSED":
      return keyPressed(state, action);
    case "MOUSE DOWN":
      return mouseDown(state, action);
    case "MOUSE LEAVE":
      return mouseLeave(state, action);
    case "MOUSE MOVE":
      return mouseMove(state, action);
    case "MOUSE UP":
      return mouseUp(state, action);
    case "RESET ZONES":
      return resetZones(state, action);
    case "SAVE PAGE":
      return savePage(state, action);
    case "SELECT PAGE":
      return selectPage(state, action);
    case "SET TABLE CELLS":
      return setTableCells(state, action);
    case "SET EDITING TABLE":
      return setEditingTable(state, action);
    case "SET NOISE REDUCTION LEVEL":
      return setNoiseReductionLevel(state, action);
    case "STAR LABEL":
      return starLabel(state, action);
  }
};

type ChildrenArgs = Readonly<{
  dispatch: Dispatch;
  state: State;
}>;

type Props = Readonly<{
  children: (args: ChildrenArgs) => ReactElement;
  highConfidenceThreshold: number;
  zoningTaxonomyName: string;
  initialZones: Array<Array<ApiZone>>;
  pageConfidences: Array<number>;
  pageDimensions: Array<Size>;
  readonly: boolean;
  taxonomy: ZoningTaxonomy;
}>;

const getOrderedLabels = (taxonomy: any): TaxonomyState["orderedLabels"] => {
  const toReturn: TaxonomyState["orderedLabels"] = {};
  if (typeof taxonomy !== "object" || taxonomy === null || Array.isArray(taxonomy)) {
    return toReturn;
  }
  const labels = Object.keys(taxonomy);
  for (const label of labels) {
    let group: string = "";
    if (typeof taxonomy[label] === "string") {
      group = taxonomy[label];
    } else if (
      typeof taxonomy[label] === "object" &&
      taxonomy[label] !== null &&
      !Array.isArray(taxonomy[label])
    ) {
      if (typeof taxonomy[label].group === "string") {
        group = taxonomy[label].group;
      }
    }
    group = group.trim().toLowerCase();
    switch (group) {
      case "image":
      case "math":
      case "table":
      case "text":
        if (Array.isArray(toReturn[group])) {
          toReturn[group]?.push(label.trim().toLowerCase());
        } else {
          toReturn[group] = [label.trim().toLowerCase()];
        }
        break;
      default:
        throw new Error("Invalid taxonomy format.");
    }
  }
  return toReturn;
};

export const StateManager = ({
  children,
  highConfidenceThreshold,
  zoningTaxonomyName,
  initialZones,
  pageConfidences,
  pageDimensions,
  readonly,
  taxonomy,
}: Props) => {
  const startingPage = useMemo(
    () =>
      Math.max(
        0,
        pageConfidences.findIndex(c => c <= highConfidenceThreshold),
      ),
    [highConfidenceThreshold, pageConfidences],
  );
  const initialState: State = useMemo(() => {
    let savedTaxonomyJson = localStorage.getItem("user_taxi_colors_" + zoningTaxonomyName);
    let savedStarred = localStorage.getItem("user_starred_" + zoningTaxonomyName);

    let savedTaxonomy: ZoningTaxonomy;
    if (savedTaxonomyJson) {
      savedTaxonomy = JSON.parse(savedTaxonomyJson);
      Object.keys(savedTaxonomy).forEach(key => {
        const jsonColor = savedTaxonomy[key].color;
        // We kept the rgba value in local storage but we have to make it an object again
        // so that we can access Color's functions
        savedTaxonomy[key].color = new Color(jsonColor.red, jsonColor.green, jsonColor.blue, jsonColor.alpha)
      })
    }

    const stateTaxonomy = {
      mode: Object.keys(taxonomy).length > 4 ? TaxonomyMode.Complex : TaxonomyMode.Simple,
      orderedLabels: getOrderedLabels(taxonomy),
      rawLabels: !!savedTaxonomy ? savedTaxonomy : taxonomy,
    };
    if (savedStarred && savedStarred.length > 0) {
      stateTaxonomy.orderedLabels = {
        starred: JSON.parse(savedStarred),
        ...stateTaxonomy.orderedLabels
      };
    }
    return {
      ...DEFAULT_INITIAL_STATE,
      activePage: startingPage,
      currentLabel: !readonly && Object.keys(taxonomy)[0] ? Object.keys(taxonomy)[0] : "",
      pages: pageDimensions.map((dimension, index) =>
        newPage(index, dimension, initialZones[index], stateTaxonomy),
      ),
      taxonomy: stateTaxonomy,
    };
  }, [initialZones, startingPage, taxonomy]);
  const [state, dispatch] = useReducer(reducer, initialState);
  return children({ dispatch, state });
};
